import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Game, GameSettings } from '../../models/Game';
import { GameStatus } from '../../models/GameStatus';
import { Player, PlayerPublic } from '../../models/Player';

interface GameState extends Game {
  isEstablishingConnection: boolean;
  isConnected: boolean;
  players: PlayerPublic[],
  player: Player | null,
  isWrongToken: boolean;
}

const gameInitialState: Game = {
  gameId: '',
  token: '',
  hostId: '',
  discordChannelId: '',
  gameStarted: false,
  gameProgress: 0,
  gameStartedDateTime: null,
  createDateTime: null,
  gameFinished: false,
  gameVoteFinished: false,
  status: GameStatus.NOT_CONNECTED,
  error: null,
  roundNumber: 0,
  turnNumber: 0,
  playedSize: 0,
  sentence: '',
  sentenceExplanation: '',
  revealedCards: [],
  results: [],
  winners: [],
  cardBacks: [],
  settings: {
    deckSize: 0,
    handSize: 0,
    maxScore: 0,
    maxCards: 0,
    maxRounds: 0,
    maxTurns: 0,
    cardBack: '',
    cardSet: [],
    useExistingDeck: true,
    asyncPlay: false,
    cardsHost: 'https://static.gdainti.com/dxt_cards',
    customRules: '',
    discordChannelId: '',
    notifyDiscord: true,
    notifyTelegram: true,
  },
  stats: null,
  hasNotifiedStart: false,
  gameAdmins: [],
}

const initialState: GameState = {
  ...gameInitialState,
  isEstablishingConnection: false,
  isConnected: false,
  players: [],
  player: null,
  isWrongToken: false,
}

const gameSlice = createSlice({
  name: 'game',
  initialState,
  reducers: {

    token: ((state, data) => {
      state.token = data.payload.token;
      state.gameId = data.payload.gameId;
    }),

    startConnecting: (state => {
      state.isEstablishingConnection = true;
      state.status = GameStatus.NOT_CONNECTED;
    }),

    connectionEstablished: (state => {
      state.isConnected = true;
      state.isEstablishingConnection = false;
    }),

    serverDisconnect: ((state, action) => {
      if (state.gameId && state.gameId !== action.payload.gameId) {
        return;
      }

      window.location.href = "/";
      state.isConnected = false;
      state.isEstablishingConnection = false;
    }),

    disconnect: (state => {
      state.isConnected = false;
      state.isEstablishingConnection = false;
    }),

    showLobbySettings: (state => {
      state.status = GameStatus.LOBBY_SETTINGS;
    }),

    wrongGameId: ((state, action) => {

      if (state.gameId && state.gameId !== action.payload.gameId) {
        return;
      }

      state.status = GameStatus.WRONG_GAME_ID;
    }),

    unknownToken: ((state, action) => {
      if (state.gameId && state.gameId !== action.payload.gameId) {
        return;
      }
      state.isWrongToken = action.payload.isWrongToken;
    }),

    selectMatchCard: ((state, data) => {
      if (state.player) {
        state.player.matchCardId = data.payload.matchCardId;
      }
    }),

    submitSentence: ((state, data: { payload: {
      gameId: string,
      playerId: string,
      sentence: string,
      sentenceExplanation: string,
      matchCardId: string
    } }) => {
      if (state.player) {
        state.sentence = data.payload.sentence;
        state.sentenceExplanation = data.payload.sentenceExplanation;
        state.player.matchCardId = data.payload.matchCardId;
      }
    }),

    submitMatch: ((state, data: { payload: {
      gameId: string,
      playerId: string,
      matchCardId: string,
    } }) => {
      if (state.player) {
        state.player.data.hasMatched = true;
        state.player.matchCardId = data.payload.matchCardId;
      }
    }),

    submitFinished: ((state, data: { payload: {
      gameId: string,
      playerId: string,
    } }) => {
      if (state.player) {
        state.player.data.hasFinished = true;
      }
    }),

    submitStoryteller: ((state, data: { payload: {
      gameId: string,
      playerId: string,
    } }) => {
    }),

    submitVote: ((state, data: { payload: {
      gameId: string,
      playerId: string,
      voteCardId: string,
    } }) => {
      if (state.player) {
        state.player.data.hasVoted = true;
        state.player.voteCardId = data.payload.voteCardId;
      }
    }),
    submitComment: ((state, data: { payload: {
      gameId: string,
      playerId: string,
      cardId: string,
      comment: string,
    } }) => {
      if (state.player) {
        if (state.player.cardComments) {
          state.player.cardComments[data.payload.cardId] = data.payload.comment;
        }
      }
    }),
    selectSentenceCard: ((state, data) => {
      if (state.player) {
        state.player.matchCardId = data.payload;
      }
    }),

    submitGameVoteFinished: ((state, data: { payload: {
      gameId: string,
      playerId: string,
    } }) => {
      if (state.player) {
        state.player.data.hasSentenceVoteFinished = true;
      }
    }),

    submitGameVoteToggle: ((state, data: { payload: {
      gameId: string,
      playerId: string,
      sentence: string
    } }) => {
      // update from socket update
    }),

    sendCardView: ((state, data: { payload: {
      gameId: string,
      playerId: string | null,
    } }) => {
    }),

    sendCardArrow: ((state, data: { payload: {
      gameId: string,
      playerId: string | null,
    } }) => {
    }),

    changeAvatar: ((state, data: { payload: {
      gameId: string | null,
      playerId: string,
    } }) => {
    }),

    // send
    join: ((state, action: PayloadAction<{
      gameId: string | undefined,
      token: string,
    }>) => {
      return;
    }),

    sendStart: ((state, action: PayloadAction<{
      gameId: string,
      playerId: string,
    }>) => {
      return;
    }),

    sendCreateNewGame: ((state, action: PayloadAction<{
    }>) => {
      return;
    }),

    sendRestart: ((state, action: PayloadAction<{
      gameId: string,
      playerId: string | undefined,
      disableNotifications: boolean
    }>) => {
      return;
    }),

    sendUser: ((state, action: PayloadAction<{
      id?: string,
      name: string
      discordId?: string
      telegramId?: string
      soundsEnabled?: boolean,
      confirmActionEnabled?: boolean,
      dixitAvatar?: string,
      directNotificationsEnabled?: boolean,
    }>) => {
      return;
    }),

    sendToken: ((state, action: PayloadAction<{
      token: string,
      gameId: string | undefined,
      isSettings?: boolean
    }>) => {
      return;
    }),

    requestUserStats: ((state, action: PayloadAction<{
      playerId: string
    }>) => {
      return;
    }),
    requestPlayersList: ((state) => {
      return;
    }),

    sendEndSession: ((state, action: PayloadAction<{
      gameId: string
      adminId: string,
    }>) => {
      return;
    }),

    sendKick: ((state, action: PayloadAction<{
      gameId: string
      playerId: string,
      adminId: string,
    }>) => {
      return;
    }),

    sendNotifyStart: ((state, action: PayloadAction<{
      gameId: string
      playerId: string
    }>) => {
      return;
    }),

    sendSave: ((state, action: PayloadAction<{
      gameId: string
      adminId: string
    }>) => {
      return;
    }),

    sendSettings: ((state, action: PayloadAction<{
      gameId: string,
      playerId?: string,
      setting: Partial<GameSettings>
    }>) => {
      state.settings = Object.assign(state.settings, action.payload.setting);
    }),

    updateSettings: ((state, action: PayloadAction<{
      gameId: string,
      playerId?: string,
      setting: Partial<GameSettings>
    }>) => {
      state.settings = Object.assign(state.settings, action.payload.setting);
    }),

    cleanUpGame: ((state) => {
      state.player = null;
    }),

    // receive
    receiveGame: ((state, action: PayloadAction<any>) => {
      if (action.payload.game) {

        if (state.gameId && state.gameId !== action.payload.game.gameId) {
          return;
        }

        state = Object.assign(state, {...action.payload.game});
      }
      else {
        state.gameId = '';
        state.status = GameStatus.MATCHMAKING;
      }
      state.players = action.payload.players;
    }),

    receivePlayer: ((state, action: PayloadAction<any>) => {

      if (state.gameId && state.gameId !== action.payload.gameId) {
        return;
      }

      if (state.player && state.player.data.playerId !== action.payload.player.data.playerId) {
        return;
      }

      state.player = action.payload.player;
    }),
  },
});

export const gameActions = gameSlice.actions;
export default gameSlice.reducer;